<template>
    <RowCell
        v-for="column in columns"
        :key="column.key"
        :source="source"
        :column="column"
        :columnWidth="columnsWidth[column.key]"
    >
        <component :is="slots?.[column.key]" v-bind="{ ...source, index }" />
    </RowCell>
    <div
        v-if="fixedColumns.left?.length"
        :class="['pt-table-row_fixed', 'pt-table-row_fixed-left', { ['reach-border']: scrollInfo.scrollLeft === 0 }]"
        :style="{ transform: `translate3d(${scrollInfo.scrollLeft}px, 0, 0)` }"
    >
        <RowCell
            v-for="column in fixedColumns.left"
            :key="'fixed' + column.key"
            :source="source"
            :column="column"
            :columnWidth="columnsWidth[column.key]"
        >
            <component :is="slots?.[column.key]" v-bind="{ ...source, index }" />
        </RowCell>
    </div>
    <div
        v-if="fixedColumns.right?.length"
        :class="[
            'pt-table-row_fixed',
            'pt-table-row_fixed-right',
            {
                ['reach-border']: scrollInfo.scrollLeft === scrollInfo.maxScrollLeft,
                ['row-right-tools']: fixedColumns.hasTools,
            },
        ]"
        :style="{ transform: `translate3d(-${scrollInfo.maxScrollLeft - scrollInfo.scrollLeft}px, 0, 0)` }"
    >
        <RowCell
            v-for="column in fixedColumns.right"
            :key="'fixed' + column.key"
            :source="source"
            :column="column"
            :columnWidth="
                fixedColumns.hasTools
                    ? typeof toolsWidth === 'number'
                        ? `${toolsWidth}px`
                        : toolsWidth
                    : columnsWidth[column.key]
            "
        >
            <component :is="slots?.[column.key]" v-bind="{ ...source, index }" />
        </RowCell>
    </div>
</template>

<script lang="ts">
export default {
    name: 'TableRow',
};
</script>

<script lang="ts" setup>
import { inject } from 'vue';
import type { Props as TableProps } from '../table.vue';
import RowCell from './rowCell.vue';
import type { ScrollInfo } from '../../../scrollbar/src/scrollbar.vue';

interface Props {
    source: any;
    index: number;
    columns: TableProps['columns'];
    columnsWidth: Record<string, string>;
    scrollInfo: ScrollInfo;
    fixedColumns: { left: TableProps['columns']; right: TableProps['columns']; hasTools: boolean };
    canClickRow?: boolean;
    toolsWidth?: number | string;
}

defineProps<Props>();

const slots = inject('slots');
</script>

<style lang="scss">
.pt-table-row {
    position: relative;
    display: flex;
    flex-direction: row;
    width: fit-content;
    align-items: center;
    justify-content: space-around;
    font-size: 13px;
    color: #091e42;
    contain: content;
    &:not(:nth-last-of-type(1)) {
        border-bottom: 1px solid #dfe1e6;
    }
    &:hover {
        > .row-right-tools {
            opacity: 1;
        }
    }
    &.total_row {
        background-color: #fafbfc;
    }
    @supports (-moz-appearance: none) {
        display: inline-flex;
    }
}

.pt-table-row--enableClick {
    &:hover {
        background-color: #f2f7f2;
        cursor: pointer;
        > .pt-table-row_fixed {
            &:not(.row-right-tools) {
                background-color: #f2f7f2;
                cursor: pointer;
            }
        }
    }
}

.pt-table-row_fixed {
    position: absolute;
    height: 100%;
    background-color: #fff;
    contain: content;
    z-index: 22;
}

.pt-table-row_fixed-left {
    left: 0;
    border-right: 1px solid #dfe1e6;
    &.reach-border {
        border: none;
    }
}

.pt-table-row_fixed-right {
    right: 0;
    &:not(.row-right-tools) {
        border-left: 1px solid #dfe1e6;
    }
    &.row-right-tools {
        background-color: transparent;
        transition: opacity 333ms ease;
        opacity: 0;
    }
    &.reach-border {
        border: none;
    }
}
</style>
